
import React, { useState, useEffect, useCallback } from 'react';
import { WhatsAppButton } from './WhatsAppButton';
import { DynamicBackground } from './DynamicBackground';
import { CalendarIcon } from './icons/CalendarIcon';
import { AuthoritySection } from './AuthoritySection';
import { useTranslation } from '../i18n/LanguageContext';
import { customSmoothScroll, getScrollTargetPosition } from '../utils/scroll';

export const Hero: React.FC = () => {
    const { t, language } = useTranslation();
    const subtitles = t('hero.subtitles') as { text: string; author?: string }[];
    
    const [subtitleIndex, setSubtitleIndex] = useState(0);
    const [isFading, setIsFading] = useState(false);

    useEffect(() => {
        const intervalId = setInterval(() => {
            setIsFading(true);
            setTimeout(() => {
                setSubtitleIndex((prevIndex) => (prevIndex + 1) % subtitles.length);
                setIsFading(false);
            }, 500);
        }, 5000);

        return () => clearInterval(intervalId);
    }, [subtitles.length]);

    const currentSubtitle = subtitles[subtitleIndex];

    const handleArrowClick = useCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        const targetY = getScrollTargetPosition('manifesto');
        customSmoothScroll(targetY, 2000); // Scroll elegante de 2 segundos
        try { if (window.history.pushState) window.history.pushState(null, "", `#manifesto`); } catch (e) {}
    }, []);

    // Automação de Data
    const getDynamicTagline = () => {
        const now = new Date();
        const day = now.getDate();
        const monthName = now.toLocaleString(language === 'pt' ? 'pt-BR' : 'en-US', { month: 'long' });
        
        const capitalizedMonth = monthName.charAt(0).toUpperCase() + monthName.slice(1);
        
        const periodKey = day <= 15 ? 'hero.tagline.early' : 'hero.tagline.late';
        const periodText = t(periodKey);

        return (
            <>
                {t('hero.tagline.part1')} 
                <strong className="text-brand-text font-semibold">{t('hero.tagline.highlight')}</strong>
                {periodText}
                <span className="text-brand-accent font-semibold">{capitalizedMonth}</span>.
            </>
        );
    };

  return (
    <section 
      id="inicio" 
      className="relative bg-brand-primary text-brand-light min-h-screen flex flex-col items-center justify-between pt-24 pb-0 md:py-20 overflow-hidden isolate"
    >
      <div className="absolute inset-0 -z-20">
        <video
          className="w-full h-full object-cover"
          src="https://nikolasyuri.com.br/wp/wp-content/uploads/2026/08/Hero_Background.mp4"
          autoPlay
          loop
          muted
          playsInline
        >
          {t('videoPlayerError')}
        </video>
        <div className="absolute inset-0 bg-brand-primary/70"></div>
      </div>
      
      <DynamicBackground />
      
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center flex flex-col items-center flex-grow justify-center">
        <h1 className="text-5xl sm:text-6xl md:text-7xl font-serif font-extrabold mb-6 drop-shadow-lg max-w-5xl animate-on-load animate-slideInDown text-glow">
          {t('hero.title')}
        </h1>
        
        <div className="text-lg sm:text-xl text-brand-text mb-8 max-w-3xl mx-auto drop-shadow-sm h-28 flex items-center justify-center animate-on-load animate-slideInUp animation-delay-[150ms]">
            <div
                className={`transition-opacity duration-500 ease-in-out ${isFading ? 'opacity-0' : 'opacity-100'}`}
            >
                <p className="text-glow italic">
                    {currentSubtitle.text}
                </p>
                {currentSubtitle.author && (
                    <span className="block text-right text-base text-slate-400 mt-2 not-italic">- {currentSubtitle.author}</span>
                )}
            </div>
        </div>

        <div className="my-8 h-2" aria-hidden="true" />

        <div className="animate-on-load animate-slideInUp animation-delay-[450ms]">
          <WhatsAppButton 
            text={t('hero.cta')}
            IconComponent={CalendarIcon}
            className="px-6 py-3 text-base sm:px-8 sm:py-4 sm:text-lg font-semibold
                       text-white 
                       border-none 
                       rounded-lg shadow-xl
                       transform hover:scale-105
                       focus:ring-2 focus:ring-offset-2 focus:ring-offset-brand-primary focus:ring-gradient-end
                       transition-all duration-300 ease-in-out
                       button-glow"
            ariaLabel={t('hero.ctaAriaLabel')}
          />
          <p className="text-sm text-brand-text/80 mt-4 animate-on-load animate-fadeIn animation-delay-[600ms]" style={{ animationFillMode: 'forwards' }}>
            {getDynamicTagline()}
          </p>
        </div>
      </div>
      
      <div className="relative z-10 w-full mb-16 md:mb-24 lg:mb-32">
        <AuthoritySection />
      </div>

      <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce hidden md:block z-10 animate-on-load animate-fadeIn animation-delay-[700ms]">
        <a href="#manifesto" onClick={handleArrowClick} aria-label={t('hero.scrollAriaLabel')}>
          <svg className="w-8 h-8 text-brand-accent hover:text-white transition-colors" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
            <path d="M19 9l-7 7-7-7"></path>
          </svg>
        </a>
      </div>
    </section>
  );
};
